
import React from "react";
import { m, AnimatePresence } from "framer-motion";
import { BiCheck, BiX, BiErrorCircle } from "react-icons/bi";
import { Button } from "@relume_io/relume-ui";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "success" | "error";
  title: string;
  message: string;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
  type,
  title,
  message,
}) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Overlay */}
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Content */}
          <m.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative w-full max-w-md overflow-hidden rounded-[32px] bg-neutral-900 p-8 text-center shadow-2xl border border-white/10"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute right-6 top-6 text-white/30 hover:text-white transition-colors"
            >
              <BiX size={24} />
            </button>

            {/* Icon Container */}
            <div className="mb-6 flex justify-center">
              <m.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 500 }}
                className={`flex h-24 w-24 items-center justify-center rounded-full ${
                  type === "success" 
                    ? "bg-brand-cyan/20 text-brand-cyan shadow-[0_0_40px_rgba(0,229,255,0.3)]" 
                    : "bg-red-500/20 text-red-500 shadow-[0_0_40px_rgba(239,68,68,0.3)]"
                }`}
              >
                {type === "success" ? (
                  <BiCheck size={56} />
                ) : (
                  <BiErrorCircle size={56} />
                )}
              </m.div>
            </div>

            {/* Text */}
            <h3 className="mb-3 text-2xl font-black text-white uppercase italic tracking-wider">
              {title}
            </h3>
            <p className="mb-8 text-white/70 leading-relaxed font-medium">
              {message}
            </p>

            {/* Action */}
            <Button
              onClick={onClose}
              className={`w-full py-4 text-sm font-black uppercase tracking-widest rounded-2xl transition-all ${
                type === "success"
                  ? "bg-brand-cyan text-black hover:bg-white"
                  : "bg-white text-black hover:bg-red-500 hover:text-white"
              }`}
            >
              {type === "success" ? "Excelente!" : "Tentar Novamente"}
            </Button>
          </m.div>
        </div>
      )}
    </AnimatePresence>
  );
};
